import React, { FC, useMemo, useState } from "react";
import { Tooltip } from "antd";
import moment from "moment";
import { BeamCamera, BeamCameraAlert } from "./types";
import BeamIcon from "./BeamIcon";
import CameraVideoDisplay from "./CameraVideoDisplay";
import { DEMO_CAMERAS, demoCameraAlerts } from "./demoData";
import "./MessageTypes.scss";

/**
 * Cameras message type — the security-monitoring view of a page.
 *
 * The operator's question is never "list my cameras", it is "which one needs me
 * right now". So the grid is ordered by that answer rather than by name or by
 * when the camera was added: a camera carrying an unacknowledged alert is
 * lifted to the front and flagged, and the rest keep their normal order behind
 * it. Nothing is hidden — the ordering changes, not the contents.
 *
 * Acknowledging is local to this session. The alert itself lives in the page
 * chat, where it is already read, replied to and resolved; duplicating that
 * lifecycle in a second store would give the page two disagreeing answers about
 * whether an alert is still open.
 */

interface CamerasCardProps {
  cameras: BeamCamera[];
  alerts: BeamCameraAlert[];
  /** Page ids are numeric in the store; the video service wants them in a URL. */
  pageId?: string | number;
  socket: ISocket;
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: "#FF5F25",
  warning: "#FBAE16",
  info: "#508afa",
};

const SEVERITY_RANK: Record<string, number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

const CamerasCard: FC<CamerasCardProps> = ({
  cameras: pageCameras,
  alerts: pageAlerts,
  pageId,
  socket,
}) => {
  const [openCameraId, setOpenCameraId] = useState<string>("");
  const [acknowledged, setAcknowledged] = useState<string[]>([]);

  /*
    A page with no camera on it has nothing to show, which is correct and
    useless at the same time — the grid cannot be reviewed, and neither can the
    flagging that is the whole point of it. Standing in for the missing devices
    keeps the screen reviewable; the badge in the header is what keeps it
    honest. One real camera and the fallback is gone entirely.
  */
  const isDemo = !pageCameras.length;
  const cameras = isDemo ? DEMO_CAMERAS : pageCameras;
  // Ages are read off the mount, so they stay minutes rather than creeping
  // into hours while a tab is left open.
  const demoAlerts = useMemo(() => demoCameraAlerts(), []);
  const alerts = isDemo ? demoAlerts : pageAlerts;

  /** Newest first, so "the alert on this camera" is the current one. */
  const alertsByCamera = useMemo(() => {
    const map: Record<string, BeamCameraAlert[]> = {};
    [...alerts]
      .sort((a, b) => moment(b.occurredAt).valueOf() - moment(a.occurredAt).valueOf())
      .forEach((alert) => {
        if (!alert.cameraId) return;
        (map[alert.cameraId] = map[alert.cameraId] || []).push(alert);
      });
    return map;
  }, [alerts]);

  const liveAlertFor = (cameraId: string): BeamCameraAlert | undefined =>
    (alertsByCamera[cameraId] || []).find(
      (alert) => acknowledged.indexOf(alert.id) === -1
    );

  /**
   * Flagged cameras first, most severe first, then most recent. Everything
   * after them keeps the order the stream gave.
   */
  const ordered = useMemo(() => {
    return [...cameras].sort((a, b) => {
      const alertA = liveAlertFor(a.id);
      const alertB = liveAlertFor(b.id);
      if (alertA && !alertB) return -1;
      if (!alertA && alertB) return 1;
      if (alertA && alertB) {
        const rank =
          SEVERITY_RANK[alertA.severity] - SEVERITY_RANK[alertB.severity];
        if (rank !== 0) return rank;
        return (
          moment(alertB.occurredAt).valueOf() -
          moment(alertA.occurredAt).valueOf()
        );
      }
      return 0;
    });
  }, [cameras, alertsByCamera, acknowledged]);

  const flaggedCount = cameras.filter((camera) => liveAlertFor(camera.id)).length;

  const openCamera = cameras.find((camera) => camera.id === openCameraId);

  if (openCamera) {
    return (
      <CameraVideoDisplay
        camera={openCamera}
        alerts={(alertsByCamera[openCamera.id] || []).filter(
          (alert) => acknowledged.indexOf(alert.id) === -1
        )}
        pageId={pageId}
        socket={socket}
        demo={isDemo}
        onBack={() => setOpenCameraId("")}
      />
    );
  }

  return (
    <section className="mt-card mt-cameras">
      <header className="mt-card__head">
        <h4>Cameras</h4>
        <div className="mt-cameras__summary">
          {isDemo && <span className="mt-demo-badge">Demo data</span>}
          <span>{cameras.length} online</span>
          {flaggedCount > 0 && (
            <span className="mt-cameras__summary-flag">
              <BeamIcon name="flag" size={13} /> {flaggedCount} flagged
            </span>
          )}
        </div>
      </header>

      {isDemo && (
        <p className="mt-demo-note">
          No camera has published to this page yet — these are sample cameras
          and sample AI alerts, shown so the view can be reviewed. They are
          replaced the moment a real camera connects.
        </p>
      )}

      <div className="mt-card__body mt-cameras__grid">
        {ordered.map((camera) => {
          const alert = liveAlertFor(camera.id);
          const color = alert ? SEVERITY_COLORS[alert.severity] : undefined;

          return (
            <article
              key={camera.id}
              className={`mt-camera-tile ${alert ? "is-flagged" : ""}`}
              style={alert ? { borderColor: color } : undefined}
              onClick={() => setOpenCameraId(camera.id)}
            >
              <div className="mt-camera-tile__frame">
                {camera.snapshotUrl ? (
                  <img src={camera.snapshotUrl} alt={camera.name} />
                ) : (
                  <span className="mt-camera-tile__noframe">
                    <BeamIcon name="camera" size={22} />
                  </span>
                )}

                {alert && (
                  <span
                    className="mt-camera-tile__flag"
                    style={{ background: color }}
                  >
                    <BeamIcon name="flag" size={12} /> {alert.type}
                  </span>
                )}

                <span className="mt-camera-tile__open">
                  Open video display
                </span>
              </div>

              <div className="mt-camera-tile__meta">
                <div>
                  <strong>{camera.name}</strong>
                  {camera.location && <span>{camera.location}</span>}
                </div>
                {alert ? (
                  <Tooltip
                    title={moment(alert.occurredAt).format(
                      "D MMM YYYY, h:mm:ss A"
                    )}
                  >
                    <button
                      type="button"
                      className="mt-btn mt-btn--ghost mt-btn--tiny"
                      onClick={(event) => {
                        event.stopPropagation();
                        setAcknowledged((previous) => [...previous, alert.id]);
                      }}
                    >
                      Clear flag
                    </button>
                  </Tooltip>
                ) : (
                  <span className="mt-camera-tile__clear">Clear</span>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default CamerasCard;
